"use client";

import { type ReactNode } from "react";

import { Icon, type IconName } from "@/components/ui/icon";

/**
 * Small status pill — the canonical chip used across the app (org settings
 * member/role status, enrollments table status / outcome / engagement, etc.).
 *
 * Colours are passed as Tailwind class strings (`bg` / `border` / `text`) so
 * each call site controls the palette while the shape stays identical:
 * `rounded-md` (NOT a full pill), hairline border, 12px medium label. Pass an
 * optional leading `icon` for engagement-style chips; omit it for plain
 * status/outcome chips.
 */
export function Pill({
  bg,
  border,
  text,
  icon,
  children,
}: {
  bg: string;
  border: string;
  text: string;
  icon?: IconName;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center justify-center gap-0.5 rounded-md border overflow-clip p-0.5 shrink-0 ${bg} ${border}`}
    >
      {icon && <Icon name={icon} size={14} className={`shrink-0 ${text}`} />}
      <span
        className={`px-0.5 text-xs font-medium leading-4 whitespace-nowrap ${text}`}
      >
        {children}
      </span>
    </span>
  );
}
